import { type ChangeEvent, type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { routes } from '../../routes';
import { SessionApiError } from '../../session/client';
import { type SessionUser } from '../../session/SessionProvider';
import { bookingHref } from '../bookings/BookingsPage';
import { getConsultation, getListing } from '../bookings/bookings-api';
import { stamp } from '../bookings/booking-model';
import { orderHref } from '../orders/OrdersPage';
import { BackLink } from '../panels';
import {
  type Order,
  type PharmacyChoice,
  type PrescriptionRequest,
  type UploadType,
  cancelRequest,
  consultationShops,
  downloadDocument,
  getHandover,
  getOrdersById,
  getRequest,
  listConsultations,
  listRequests,
  pharmaciesNear,
  sendRequest,
  uploadPrescription,
} from './prescriptions-api';
import {
  type Medicine,
  type Step,
  type StripIcon,
  TYPE_LABEL,
  UPLOAD_TYPE_LABEL,
  prescriptionsPath,
  privateMedicine,
  privateState,
  privateSteps,
  requestMedicine,
  requestName,
  requestSteps,
  splitMedicines,
} from './rx-model';
import '../orders/orders.css';
import '../bookings/bookings.css';
import './prescriptions.css';

/**
 * The Prescriptions page — the `Just Treated Prescriptions` frame, over the
 * platform's own records (website stage 10, owner decision D10 phase 1).
 *
 * "Your medicines" holds the person's private prescriptions (online
 * consultations and the orders they were paid on) and the requests they have
 * sent a pharmacy: a repeat, or an uploaded prescription. The pharmacy moves a
 * request on from its dashboard; a ready one carries a collection code.
 *
 * The frame's NHS parts — nominated pharmacy, NHS number and GP, "Verified",
 * the repeat list with "4 of 6 left", "next due", the £9.90 NHS charge — need
 * the NHS Spine and are drawn as "Coming soon" or left out, never with the
 * frame's demo values. So are "How to take it" (no label record), "Deliver it
 * instead", secure messaging and the Freepost address.
 */

const IC = {
  rx: 'M9 3h6v4H9zM6 5h12v16H6zM9 12h6M12 9v6',
  redo: 'M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4',
  upload: 'M12 16V4M7 9l5-5 5 5M4 16v4h16v-4',
  chat: 'M4 5h16v11H8l-4 4Z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2',
  store: 'M3 10h18l-1.5-5h-15L3 10ZM5 10v10h14V10M10 20v-5h4v5',
  truck: 'M3 16V8h11v8M14 11h4l3 3v2h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  check: 'm5 12.5 4.2 4.2L19 7',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5M12 16.5h.01',
  file: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6',
  stop: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 12h8',
  card: 'M3.5 6.5h17v11h-17zM3.5 10h17M7 14.5h4',
  bag: 'M5 8h14l-1 12H6ZM9 8V6a3 3 0 0 1 6 0v2',
  cal: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  x: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9 9l6 6M15 9l-6 6',
  back: 'M14.6 5.4 8 12l6.6 6.6',
  next: 'M9.4 5.4 16 12l-6.6 6.6',
  plus: 'M12 5v14M5 12h14',
} as const;

const STRIP_ICON: Readonly<Record<StripIcon, string>> = {
  clock: IC.clock,
  store: IC.store,
  redo: IC.redo,
  mail: IC.mail,
  rx: IC.rx,
  stop: IC.stop,
  chat: IC.chat,
  card: IC.card,
  check: IC.check,
  truck: IC.truck,
};

const TYPE_ICON = { private: IC.rx, upload: IC.upload, repeat: IC.redo } as const;

function Icon({
  d,
  stroke = 'currentColor',
  size = 17,
  width = 2,
}: {
  readonly d: string;
  readonly stroke?: string;
  readonly size?: number;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-ord-soon">Coming soon</span>;
}

function messageOf(error: unknown): string {
  if (error instanceof SessionApiError) return error.message;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Something went wrong. Try again.';
}

// -- Small hooks ------------------------------------------------------------------------

interface Loaded<T> {
  readonly value?: T;
  readonly error?: Error;
  readonly loading: boolean;
  reload(quiet?: boolean): void;
}

function useLoaded<T>(load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<{ value?: T; error?: Error; loading: boolean }>({ loading: true });
  const loadRef = useRef(load);
  loadRef.current = load;
  const alive = useRef(true);

  const reload = useCallback((quiet = false) => {
    if (!quiet) setState((previous) => ({ ...previous, loading: true }));
    loadRef
      .current()
      .then((value) => {
        if (alive.current) setState({ value, loading: false });
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        const failure = error instanceof Error ? error : new Error('Something went wrong.');
        setState((previous) =>
          quiet && previous.value !== undefined ? previous : { error: failure, loading: false },
        );
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { ...state, reload };
}

function useNarrow(): boolean {
  const read = (): boolean => (typeof window === 'undefined' ? false : window.innerWidth < 940);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = (): void => setNarrow(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function useToast(): [string, (message: string) => void] {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(''), 2800);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, flash];
}

function Toast({ message }: { readonly message: string }): JSX.Element | null {
  return message === '' ? null : (
    <div className="jt-ord-toast" role="status">
      {message}
    </div>
  );
}

// -- Page --------------------------------------------------------------------------------

export function PrescriptionsPage({ user }: { readonly user: SessionUser }): JSX.Element {
  const navigator = useNavigator();
  const path = navigator.location.path;
  const prefix = `${prescriptionsPath}/`;
  if (path.startsWith(prefix)) {
    const id = decodeURIComponent(path.slice(prefix.length));
    return navigator.location.query.get('kind') === 'consultation' ? (
      <PrivateDetail key={id} consultationId={id} />
    ) : (
      <RequestDetail key={id} requestId={id} />
    );
  }
  return <PrescriptionsList user={user} />;
}

// -- The list --------------------------------------------------------------------------------

interface ListData {
  readonly medicines: readonly Medicine[];
  readonly pharmacies: readonly PharmacyChoice[];
  readonly consultationsFailed: boolean;
}

async function loadList(now: number): Promise<ListData> {
  const [requests, consultations] = await Promise.all([
    listRequests(),
    listConsultations().catch(() => undefined),
  ]);
  const shops =
    consultations !== undefined && consultations.length > 0
      ? await consultationShops(consultations)
      : new Map();
  const orders =
    consultations?.some((c) => c.orderId !== undefined) === true
      ? await getOrdersById()
      : new Map<string, Order>();

  const medicines: Medicine[] = [
    ...requests.map(requestMedicine),
    ...(consultations ?? []).map((c) =>
      privateMedicine(
        c,
        c.orderId !== undefined ? orders.get(c.orderId) : undefined,
        shops.get(c.storefrontId)?.name ?? 'The pharmacy',
        now,
      ),
    ),
  ];

  // The pharmacies this person already uses: where they sent requests, and
  // where their consultations were. Never a made-up "nominated" one.
  const pharmacies = new Map<string, PharmacyChoice>();
  for (const r of requests) pharmacies.set(r.storefront.id, { id: r.storefront.id, name: r.storefront.name });
  for (const [storefrontId, shop] of shops) {
    pharmacies.set(storefrontId, {
      id: storefrontId,
      name: shop.name,
      ...(shop.place !== undefined ? { place: shop.place } : {}),
    });
  }
  return {
    medicines,
    pharmacies: [...pharmacies.values()],
    consultationsFailed: consultations === undefined,
  };
}

type Modal = 'upload' | 'repeat' | undefined;

function PrescriptionsList({ user }: { readonly user: SessionUser }): JSX.Element {
  const [now] = useState(() => Date.now());
  const data = useLoaded(() => loadList(now));
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [modal, setModal] = useState<Modal>();
  const [toast, flash] = useToast();
  const navigator = useNavigator();

  const { active, history } = splitMedicines(data.value?.medicines ?? []);
  const list = tab === 'history' ? history : active;

  const starts: readonly { t: string; d: string; icon: string; bg: string; fg: string; act: () => void }[] = [
    {
      t: 'Order a repeat',
      d: 'Ask a pharmacy to get a repeat from your GP',
      icon: IC.redo,
      bg: '#e7f2fb',
      fg: '#005eb8',
      act: () => setModal('repeat'),
    },
    {
      t: 'Upload a prescription',
      d: 'Private, paper NHS or vet prescriptions',
      icon: IC.upload,
      bg: '#fff1d6',
      fg: '#b86e00',
      act: () => setModal('upload'),
    },
    {
      t: 'Online consultation',
      d: 'Get a private prescription from a UK prescriber',
      icon: IC.chat,
      bg: '#fdeef0',
      fg: '#c0396a',
      act: () => navigator.navigate(routes.nearby.path),
    },
  ];

  let body: ReactNode;
  if (data.value === undefined && data.loading) {
    body = (
      <div className="jt-ord-panel" aria-busy="true">
        <span className="jt-ord-panel__copy">Loading your prescriptions…</span>
      </div>
    );
  } else if (data.value === undefined) {
    body = (
      <div className="jt-ord-panel" role="alert">
        <span className="jt-ord-panel__head">We couldn’t load your prescriptions</span>
        <span className="jt-ord-panel__copy">{messageOf(data.error)}</span>
        <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={() => data.reload()}>
          Try again
        </button>
      </div>
    );
  } else if (list.length === 0) {
    body = (
      <div className="jt-ord-empty">
        <span className="jt-ord-empty__icon" aria-hidden="true">
          <Icon d={IC.rx} stroke="#5b2ede" size={26} />
        </span>
        <span className="jt-ord-empty__title">
          {tab === 'history' ? 'Nothing here yet' : 'No prescriptions yet'}
        </span>
        <span className="jt-ord-empty__sub">
          {tab === 'history'
            ? 'Prescriptions you’ve finished will show here.'
            : 'Upload a prescription, ask a pharmacy for a repeat, or start an online consultation.'}
        </span>
      </div>
    );
  } else {
    body = (
      <div className="jt-ord-cards">
        {list.map((medicine) => (
          <MedicineCard key={medicine.key} medicine={medicine} />
        ))}
      </div>
    );
  }

  const done = (message: string): void => {
    setModal(undefined);
    setTab('active');
    flash(message);
    data.reload(true);
  };

  return (
    <>
      <BackLink />
      <div className="jt-ord-listhead">
        <div className="jt-ord-listhead__text">
          <h1 className="jt-ord-h1">Prescriptions</h1>
          <span className="jt-ord-listhead__sub">Private prescriptions, uploads and repeat requests</span>
        </div>
      </div>

      <section aria-label="Your NHS details" className="jt-rx-nhs">
        <div className="jt-rx-nhs__row">
          <span className="jt-rx-nhs__tile jt-rx-nhs__tile--pharmacy" aria-hidden="true">
            <Icon d={IC.store} stroke="#fff" size={20} />
          </span>
          <span className="jt-rx-nhs__text">
            <span className="jt-rx-nhs__label">NOMINATED PHARMACY</span>
            <span className="jt-rx-nhs__value">Choosing your NHS pharmacy</span>
          </span>
          <Soon />
        </div>
        <div className="jt-rx-nhs__row">
          <span className="jt-rx-nhs__tile jt-rx-nhs__tile--nhs" aria-hidden="true">
            NHS
          </span>
          <span className="jt-rx-nhs__text">
            <span className="jt-rx-nhs__label">NHS NUMBER · GP</span>
            <span className="jt-rx-nhs__value">Linking your NHS record</span>
          </span>
          <Soon />
        </div>
      </section>

      <div className="jt-rx-starts">
        {starts.map((start) => (
          <button key={start.t} type="button" className="jt-rx-start" onClick={start.act}>
            <span className="jt-rx-start__icon" style={{ background: start.bg }} aria-hidden="true">
              <Icon d={start.icon} stroke={start.fg} size={22} />
            </span>
            <span className="jt-rx-start__text">
              <span className="jt-rx-start__title">{start.t}</span>
              <span className="jt-rx-start__sub">{start.d}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="jt-rx-medhead">
        <h2 className="jt-rx-medhead__title">Your medicines</h2>
        <div role="tablist" aria-label="Prescriptions" className="jt-ord-tabs">
          {(
            [
              ['active', `Active · ${String(active.length)}`],
              ['history', `History · ${String(history.length)}`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`jt-ord-tab${tab === id ? ' jt-ord-tab--on' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {body}
      {data.value?.consultationsFailed === true ? (
        <p className="jt-ord-note" role="status">
          Your online consultations couldn’t be loaded just now, so only your requests are shown.
        </p>
      ) : null}

      {modal === 'upload' ? (
        <UploadModal
          user={user}
          pharmacies={data.value?.pharmacies ?? []}
          onClose={() => setModal(undefined)}
          onSent={(request) => done(`Sent to ${request.storefront.name}. Now hand in or post the original.`)}
        />
      ) : null}
      {modal === 'repeat' ? (
        <RepeatModal
          pharmacies={data.value?.pharmacies ?? []}
          onClose={() => setModal(undefined)}
          onSent={(request) => done(`Sent to ${request.storefront.name}. We’ll show each step here.`)}
        />
      ) : null}
      <Toast message={toast} />
    </>
  );
}

function MedicineCard({ medicine }: { readonly medicine: Medicine }): JSX.Element {
  const navigator = useNavigator();
  const type = TYPE_LABEL[medicine.type];
  const reply = medicine.type === 'private' && medicine.badge.label === 'REPLY NEEDED';
  return (
    <article className="jt-ord-card">
      <div className="jt-ord-card__top">
        <span className="jt-rx-type" style={{ background: type.bg }} aria-hidden="true">
          <Icon d={TYPE_ICON[medicine.type]} stroke={type.fg} size={22} />
        </span>
        <span className="jt-rx-card__text">
          <span className="jt-rx-card__type">{type.label}</span>
          <span className="jt-rx-card__name">{medicine.name}</span>
          <span className="jt-rx-card__meta">{medicine.meta}</span>
        </span>
        <span
          className="jt-ord-badge jt-ord-badge--card"
          style={{ background: medicine.badge.bg, color: medicine.badge.fg }}
        >
          {medicine.badge.label}
        </span>
      </div>
      <div className="jt-ord-strip" style={{ background: medicine.strip.bg, color: medicine.strip.fg }}>
        <Icon d={STRIP_ICON[medicine.strip.icon]} />
        <span>{medicine.strip.text}</span>
      </div>
      <div className="jt-ord-card__actions">
        <a
          href={medicine.href}
          className="jt-ord-pill jt-ord-pill--ink"
          onClick={(event) => {
            event.preventDefault();
            navigator.navigate(medicine.href);
          }}
        >
          {medicine.ready ? 'Show collection code' : 'View details'}
        </a>
        {reply ? (
          <AppLink
            to={bookingHref(medicine.key.slice(2), 'consultation')}
            className="jt-ord-pill jt-ord-pill--soft"
          >
            Reply to prescriber
          </AppLink>
        ) : (
          <HelpPill />
        )}
      </div>
    </article>
  );
}

function HelpPill(): JSX.Element {
  return routes.helpCentre.availability === 'available' ? (
    <AppLink to={routes.helpCentre.path} className="jt-ord-pill jt-ord-pill--soft">
      Get help
    </AppLink>
  ) : (
    <span className="jt-ord-pill jt-ord-pill--soft jt-ord-pill--off" aria-disabled="true">
      Get help <Soon />
    </span>
  );
}

// -- Shared detail parts --------------------------------------------------------------------

interface Action {
  readonly label: string;
  readonly icon: string;
  readonly danger?: boolean;
  readonly act?: () => void;
}

function Actions({ actions }: { readonly actions: readonly Action[] }): JSX.Element {
  return (
    <div className="jt-ord-actions">
      {actions.map((action) =>
        action.act !== undefined ? (
          <button
            key={action.label}
            type="button"
            className={`jt-ord-action${action.danger === true ? ' jt-ord-action--danger' : ''}`}
            onClick={action.act}
          >
            <span className="jt-ord-action__icon">
              <Icon d={action.icon} stroke={action.danger === true ? '#a81f1f' : '#5b2ede'} />
            </span>
            <span className="jt-ord-action__label">{action.label}</span>
            <Icon d={IC.next} stroke="#8a7fb5" size={14} width={2.6} />
          </button>
        ) : (
          <div key={action.label} className="jt-ord-action jt-ord-action--off" aria-disabled="true">
            <span className="jt-ord-action__icon">
              <Icon d={action.icon} stroke="#8a7fb5" />
            </span>
            <span className="jt-ord-action__label">{action.label}</span>
            <Soon />
          </div>
        ),
      )}
    </div>
  );
}

/** Get help: the Help page once it is live, otherwise an inert "Coming soon". */
function helpAction(navigate: (to: string) => void): Action {
  return routes.helpCentre.availability === 'available'
    ? { label: 'Get help', icon: IC.help, act: () => navigate(routes.helpCentre.path) }
    : { label: 'Get help', icon: IC.help };
}

function AllLink(): JSX.Element {
  const navigator = useNavigator();
  return (
    <a
      href={prescriptionsPath}
      className="jt-site-back"
      onClick={(event) => {
        event.preventDefault();
        navigator.navigate(prescriptionsPath);
      }}
    >
      <Icon d={IC.back} size={15} width={2.6} />
      All prescriptions
    </a>
  );
}

function DetailState({
  loading,
  error,
  onRetry,
}: {
  readonly loading: boolean;
  readonly error: Error | undefined;
  readonly onRetry: () => void;
}): JSX.Element {
  const missing = error instanceof SessionApiError && error.status === 404;
  return (
    <>
      <AllLink />
      {loading ? (
        <div className="jt-ord-panel" aria-busy="true">
          <span className="jt-ord-panel__copy">Loading your prescription…</span>
        </div>
      ) : (
        <div className="jt-ord-panel" role="alert">
          <span className="jt-ord-panel__head">
            {missing ? 'We couldn’t find this prescription' : 'We couldn’t load this prescription'}
          </span>
          <span className="jt-ord-panel__copy">
            {missing ? 'It isn’t one of yours, or the link is wrong.' : messageOf(error)}
          </span>
          {missing ? null : (
            <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      )}
    </>
  );
}

function Steps({ steps }: { readonly steps: readonly Step[] }): JSX.Element {
  return (
    <ol aria-label="Progress" className="jt-ord-steps">
      {steps.map((step, index) => (
        <li
          key={step.label}
          className={`jt-ord-step jt-ord-step--${step.state === 'bad' ? 'cancelled' : step.state}${
            index === steps.length - 1 ? ' jt-ord-step--last' : ''
          }`}
        >
          <span className="jt-ord-step__rail">
            <span className="jt-ord-step__dot">
              {step.state === 'done' ? <Icon d={IC.check} stroke="#fff" size={12} width={3.6} /> : null}
            </span>
            <span className="jt-ord-step__line" />
          </span>
          <span className="jt-ord-step__text">
            <span className="jt-ord-step__label">{step.label}</span>
            <span className="jt-ord-step__sub">{step.sub}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function DetailLayout({
  narrow,
  medicine,
  hero,
  children,
  facts,
  actions,
}: {
  readonly narrow: boolean;
  readonly medicine: Medicine;
  readonly hero: string;
  readonly children: ReactNode;
  readonly facts: readonly { k: string; v: string }[];
  readonly actions: readonly Action[];
}): JSX.Element {
  const type = TYPE_LABEL[medicine.type];
  return (
    <div className={`jt-ord-detail${narrow ? ' jt-ord-detail--narrow' : ''}`}>
      <div className="jt-ord-detail__main">
        <AllLink />
        <section aria-label="Prescription status" className="jt-bk-main">
          <div className="jt-bk-head">
            <span className="jt-bk-head__badge" style={{ color: medicine.badge.fg }}>
              {type.label} · {medicine.badge.label}
            </span>
            <h1 className="jt-bk-head__title">{medicine.name}</h1>
            <span className="jt-bk-head__sub">{hero}</span>
          </div>
          {children}
        </section>
      </div>
      <div className="jt-ord-detail__side">
        <aside aria-label="Prescription details" className="jt-ord-summary">
          <dl className="jt-bk-facts">
            {facts.map((fact) => (
              <div key={fact.k} className="jt-bk-fact">
                <dt>{fact.k}</dt>
                <dd>{fact.v}</dd>
              </div>
            ))}
          </dl>
        </aside>
        <Actions actions={actions} />
      </div>
    </div>
  );
}

function CollectionCode({ code, store }: { readonly code: string; readonly store: string }): JSX.Element {
  return (
    <div className="jt-ord-code" aria-label="Collection code">
      <span className="jt-ord-code__text">
        <span className="jt-ord-code__label">COLLECTION CODE</span>
        <span className="jt-ord-code__value">{code}</span>
        <span className="jt-ord-code__hint">Show this at {store}.</span>
      </span>
    </div>
  );
}

// -- One request: a repeat or an upload ----------------------------------------------------------

function RequestDetail({ requestId }: { readonly requestId: string }): JSX.Element {
  const navigator = useNavigator();
  const narrow = useNarrow();
  const [toast, flash] = useToast();
  const [cancelling, setCancelling] = useState(false);
  const loaded = useLoaded(() => getRequest(requestId));

  const r = loaded.value;
  if (r === undefined) {
    return <DetailState loading={loaded.loading} error={loaded.error} onRetry={() => loaded.reload()} />;
  }
  const medicine = requestMedicine(r);
  const store = r.storefront.name;
  const latest = [...r.history].reverse().find((h) => h.message !== undefined && h.by === 'PHARMACY');
  const hero = {
    REQUESTED: `Sent to ${store}. They’ll start on it from their dashboard.`,
    WITH_GP: `${store} has sent your request to your GP. We’ll show it here when it moves on.`,
    CHECKING: 'We’ve got your photo. The pharmacist needs the original paper copy before they can supply it.',
    DISPENSING: `${store} is preparing your medicine.`,
    READY: `Ready to collect from ${store}.`,
    COMPLETED: `Collected from ${store}.`,
    DECLINED: `${store} couldn’t supply this.`,
    CANCELLED: 'You cancelled this request.',
  }[r.status];

  const facts = [
    { k: 'Reference', v: r.reference },
    { k: 'Type', v: r.kind === 'UPLOAD' ? UPLOAD_TYPE_LABEL[r.uploadType ?? 'PRIVATE'] : 'Repeat request' },
    { k: 'Pharmacy', v: store },
    ...(r.kind === 'REPEAT'
      ? r.items.map((item, index) => ({
          k: r.items.length === 1 ? 'Medicine' : `Medicine ${String(index + 1)}`,
          v: item.quantity !== undefined ? `${item.name} · ${item.quantity}` : item.name,
        }))
      : [{ k: 'File', v: r.documentName ?? 'Your upload' }]),
    ...(r.gpPractice !== undefined ? [{ k: 'Your GP', v: r.gpPractice }] : []),
    { k: 'Sent', v: stamp(r.createdAt) },
  ];

  const actions: Action[] = [];
  if (r.documentId !== undefined) {
    const documentId = r.documentId;
    actions.push({
      label: 'Download your upload',
      icon: IC.file,
      act: () => {
        void downloadDocument(documentId).then((blob) => {
          if (blob === undefined) {
            flash('That file couldn’t be opened just now');
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = r.documentName ?? 'prescription';
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        });
      },
    });
  }
  if (r.canCancel)
    actions.push({ label: 'Cancel request', icon: IC.x, danger: true, act: () => setCancelling(true) });
  actions.push({ label: 'Message the pharmacy', icon: IC.chat });
  actions.push(helpAction(navigator.navigate));

  return (
    <>
      <DetailLayout narrow={narrow} medicine={medicine} hero={hero} facts={facts} actions={actions}>
        {r.status === 'READY' && r.collectionCode !== undefined ? (
          <CollectionCode code={r.collectionCode} store={store} />
        ) : null}
        {r.status === 'CHECKING' ? (
          <div className="jt-rx-paper">
            <span className="jt-rx-paper__icon" aria-hidden="true">
              <Icon d={IC.mail} stroke="#5c3206" size={17} width={2.2} />
            </span>
            <span className="jt-rx-paper__text">
              <span className="jt-rx-paper__head">We need the original paper copy</span>
              <span className="jt-rx-paper__copy">
                The pharmacist can’t supply your medicine until they have it. Hand it in at {store}. Your
                reference is {r.reference}.
              </span>
            </span>
          </div>
        ) : null}
        {r.status === 'DECLINED' && r.declineReason !== undefined ? (
          <div className="jt-ord-strip" style={{ background: '#fbe3e3', color: '#8c1919' }}>
            <Icon d={IC.stop} />
            <span>{r.declineReason}</span>
          </div>
        ) : latest?.message !== undefined && r.status !== 'DECLINED' ? (
          <div className="jt-bk-note" style={{ background: '#f0ecfa' }}>
            <span className="jt-bk-note__avatar" aria-hidden="true">
              Rx
            </span>
            <span className="jt-bk-note__text">
              <span className="jt-bk-note__head" style={{ color: '#4a22bd' }}>
                From {store}
              </span>
              <span className="jt-bk-note__body">{latest.message}</span>
            </span>
          </div>
        ) : null}
        <Steps steps={requestSteps(r)} />
        {r.note !== undefined ? (
          <div className="jt-rx-yournote">
            <span className="jt-rx-yournote__label">YOUR NOTE</span>
            <span className="jt-rx-yournote__text">{r.note}</span>
          </div>
        ) : null}
      </DetailLayout>
      {cancelling ? (
        <CancelModal
          request={r}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            flash('Request cancelled');
            loaded.reload(true);
          }}
        />
      ) : null}
      <Toast message={toast} />
    </>
  );
}

// -- One private prescription: a consultation and its order -----------------------------------

function PrivateDetail({ consultationId }: { readonly consultationId: string }): JSX.Element {
  const navigator = useNavigator();
  const narrow = useNarrow();
  const loaded = useLoaded(async () => {
    const consultation = await getConsultation(consultationId);
    const [listing, orders] = await Promise.all([
      getListing(consultation.listingId).catch(() => undefined),
      consultation.orderId !== undefined ? getOrdersById() : Promise.resolve(new Map<string, Order>()),
    ]);
    const order = consultation.orderId !== undefined ? orders.get(consultation.orderId) : undefined;
    const code =
      order?.status === 'READY_FOR_COLLECTION'
        ? await getHandover(order.id)
            .then((h) => h.collection?.code)
            .catch(() => undefined)
        : undefined;
    return { consultation, store: listing?.storefront.name ?? 'The pharmacy', order, code };
  });

  const value = loaded.value;
  if (value === undefined) {
    return <DetailState loading={loaded.loading} error={loaded.error} onRetry={() => loaded.reload()} />;
  }
  const { consultation: c, store, order, code } = value;
  const now = Date.now();
  const state = privateState(c, order, now);
  const medicine = privateMedicine(c, order, store, now);
  const hero = {
    pay: 'You pay first. A prescriber then reviews your answers before anything is sent.',
    review: 'A prescriber is reviewing your consultation answers.',
    info: 'Your prescriber has a question for you.',
    approved: `Approved. The order goes to ${store}.`,
    dispensing: `${store} is preparing your medicine.`,
    ready: `Ready to collect from ${store}.`,
    sent: 'On its way to you.',
    complete: 'Completed.',
    declined: 'The prescriber did not approve this.',
    cancelled: 'The order was cancelled.',
    expired: 'These answers expired before an order was paid.',
  }[state];

  const facts = [
    { k: 'Consultation', v: c.reference },
    ...(order !== undefined ? [{ k: 'Order', v: order.reference }] : []),
    { k: 'Pharmacy', v: store },
    ...(order !== undefined ? [{ k: 'Price', v: money(order) }] : []),
    ...(order !== undefined
      ? [{ k: 'Fulfilment', v: order.fulfilment === 'DELIVERY' ? 'Delivery' : 'Collection' }]
      : []),
    { k: 'Answers sent', v: stamp(c.submittedAt) },
  ];

  const actions: Action[] = [];
  if (order !== undefined) {
    const orderId = order.id;
    actions.push({ label: 'View order', icon: IC.bag, act: () => navigator.navigate(orderHref(orderId)) });
  }
  actions.push(
    state === 'info'
      ? {
          label: 'Reply to prescriber',
          icon: IC.chat,
          act: () => navigator.navigate(bookingHref(c.id, 'consultation')),
        }
      : { label: 'Message the prescriber', icon: IC.chat },
  );
  actions.push({ label: 'Download prescription details', icon: IC.file });
  actions.push(helpAction(navigator.navigate));

  return (
    <DetailLayout narrow={narrow} medicine={medicine} hero={hero} facts={facts} actions={actions}>
      {state === 'ready' && code !== undefined ? <CollectionCode code={code} store={store} /> : null}
      {state === 'declined' && c.decision?.reason !== undefined ? (
        <div className="jt-ord-strip" style={{ background: '#fbe3e3', color: '#8c1919' }}>
          <Icon d={IC.stop} />
          <span>{c.decision.reason}</span>
        </div>
      ) : null}
      <a
        href={bookingHref(c.id, 'consultation')}
        className="jt-rx-consult"
        onClick={(event) => {
          event.preventDefault();
          navigator.navigate(bookingHref(c.id, 'consultation'));
        }}
      >
        <Icon d={IC.cal} stroke="#5b2ede" size={18} />
        <span className="jt-rx-consult__text">From your online consultation {c.reference}</span>
        <Icon d={IC.next} stroke="#5b2ede" size={14} width={2.6} />
      </a>
      <Steps steps={privateSteps(c, order, store, now)} />
    </DetailLayout>
  );
}

function money(order: Order): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: order.currency }).format(
      order.totalMinor / 100,
    );
  } catch {
    return `£${(order.totalMinor / 100).toFixed(2)}`;
  }
}

// -- Modals --------------------------------------------------------------------------------------

function useEscape(onClose: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

function ModalFrame({
  title,
  copy,
  children,
  onClose,
}: {
  readonly title: string;
  readonly copy?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
}): JSX.Element {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, input, textarea')?.focus();
  }, []);
  return (
    <>
      <div className="jt-ord-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className="jt-ord-modal jt-rx-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jt-rx-m-title"
      >
        <span className="jt-ord-modal__text">
          <span id="jt-rx-m-title" className="jt-ord-modal__title">
            {title}
          </span>
          {copy !== undefined ? <span className="jt-ord-modal__copy">{copy}</span> : null}
        </span>
        {children}
      </div>
    </>
  );
}

function CancelModal({
  request,
  onClose,
  onCancelled,
}: {
  readonly request: PrescriptionRequest;
  readonly onClose: () => void;
  readonly onCancelled: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const go = (): void => {
    if (busy) return;
    setBusy(true);
    setFailure(undefined);
    cancelRequest(request.id)
      .then(() => onCancelled())
      .catch((error: unknown) => {
        setFailure(messageOf(error));
        setBusy(false);
      });
  };
  return (
    <ModalFrame
      title="Cancel this request?"
      copy={`${request.storefront.name} hasn’t started on ${requestName(request)} yet, so you can still cancel it.`}
      onClose={onClose}
    >
      {failure !== undefined ? (
        <span className="jt-ord-err" role="alert">
          {failure}
        </span>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Keep request
        </button>
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--danger" disabled={busy} onClick={go}>
          {busy ? 'Cancelling…' : 'Cancel request'}
        </button>
      </div>
    </ModalFrame>
  );
}

/**
 * Which pharmacy the request goes to: the ones this person already uses, or
 * one found near a postcode from the storefront directory. Nothing is chosen
 * for them.
 */
function PharmacyPicker({
  known,
  value,
  onChange,
}: {
  readonly known: readonly PharmacyChoice[];
  readonly value: PharmacyChoice | undefined;
  readonly onChange: (choice: PharmacyChoice) => void;
}): JSX.Element {
  const [postcode, setPostcode] = useState('');
  const [found, setFound] = useState<readonly PharmacyChoice[]>([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState<string | undefined>();

  const search = (): void => {
    const code = postcode.trim();
    if (code.length < 2 || searching) return;
    setSearching(true);
    setNote(undefined);
    pharmaciesNear(code)
      .then((places) => {
        setFound(places);
        if (places.length === 0) setNote('No pharmacies on Just Treated near that postcode yet.');
      })
      .catch((error: unknown) => setNote(messageOf(error)))
      .finally(() => setSearching(false));
  };

  const options = [...known, ...found.filter((f) => !known.some((k) => k.id === f.id))];
  return (
    <fieldset className="jt-rx-field jt-rx-pick">
      <legend className="jt-rx-field__label">Send it to</legend>
      {options.length === 0 ? (
        <span className="jt-rx-field__hint">Find a pharmacy near you to send it to.</span>
      ) : (
        <div className="jt-rx-options">
          {options.map((option) => {
            const on = value?.id === option.id;
            return (
              <label key={option.id} className={`jt-rx-option${on ? ' jt-rx-option--on' : ''}`}>
                <input
                  type="radio"
                  name="jt-rx-pharmacy"
                  className="jt-rx-option__input"
                  checked={on}
                  onChange={() => onChange(option)}
                />
                <span className="jt-rx-option__text">
                  <span className="jt-rx-option__label">{option.name}</span>
                  {option.place !== undefined ? (
                    <span className="jt-rx-option__sub">{option.place}</span>
                  ) : null}
                </span>
                <span className="jt-rx-option__ring" aria-hidden="true" />
              </label>
            );
          })}
        </div>
      )}
      <span className="jt-rx-search">
        <input
          type="text"
          aria-label="Postcode"
          placeholder="Postcode, e.g. NR2 1AB"
          autoComplete="postal-code"
          value={postcode}
          maxLength={10}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setPostcode(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              search();
            }
          }}
        />
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={search} disabled={searching}>
          {searching ? 'Finding…' : 'Find'}
        </button>
      </span>
      {note !== undefined ? <span className="jt-rx-field__hint">{note}</span> : null}
    </fieldset>
  );
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}

const UPLOAD_TYPES: readonly { id: UploadType; label: string; sub: string }[] = [
  { id: 'PRIVATE', label: 'Private prescription', sub: 'From a private GP, dentist or clinic' },
  { id: 'NHS_PAPER', label: 'Paper NHS prescription', sub: 'A green FP10 form from your GP' },
  { id: 'VETERINARY', label: 'Veterinary prescription', sub: 'For a pet medicine' },
];

function UploadModal({
  user,
  pharmacies,
  onClose,
  onSent,
}: {
  readonly user: SessionUser;
  readonly pharmacies: readonly PharmacyChoice[];
  readonly onClose: () => void;
  readonly onSent: (request: PrescriptionRequest) => void;
}): JSX.Element {
  const [type, setType] = useState<UploadType>('PRIVATE');
  const [file, setFile] = useState<File | undefined>();
  const [pharmacy, setPharmacy] = useState<PharmacyChoice | undefined>(
    pharmacies.length === 1 ? pharmacies[0] : undefined,
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const [fileMissing, setFileMissing] = useState(false);

  const send = async (): Promise<void> => {
    if (busy) return;
    setFailure(undefined);
    if (file === undefined) {
      setFileMissing(true);
      return;
    }
    if (pharmacy === undefined) {
      setFailure('Choose the pharmacy to send it to.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFailure('That file is too large. Send one under 10 MB.');
      return;
    }
    setBusy(true);
    try {
      const uploaded = await uploadPrescription(user.personId, file.name, await readBase64(file));
      const sent = await sendRequest({
        kind: 'UPLOAD',
        storefrontId: pharmacy.id,
        uploadType: type,
        documentId: uploaded.id,
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });
      onSent(sent);
    } catch (error) {
      setFailure(messageOf(error));
      setBusy(false);
    }
  };

  return (
    <ModalFrame
      title="Upload a prescription"
      copy="A photo gets things started. The pharmacist will still need the original paper copy."
      onClose={onClose}
    >
      <fieldset className="jt-rx-field">
        <legend className="jt-rx-field__label">What kind is it?</legend>
        <div className="jt-rx-options">
          {UPLOAD_TYPES.map((option) => {
            const on = type === option.id;
            return (
              <label key={option.id} className={`jt-rx-option${on ? ' jt-rx-option--on' : ''}`}>
                <input
                  type="radio"
                  name="jt-rx-type"
                  className="jt-rx-option__input"
                  checked={on}
                  onChange={() => setType(option.id)}
                />
                <span className="jt-rx-option__text">
                  <span className="jt-rx-option__label">{option.label}</span>
                  <span className="jt-rx-option__sub">{option.sub}</span>
                </span>
                <span className="jt-rx-option__ring" aria-hidden="true" />
              </label>
            );
          })}
        </div>
      </fieldset>
      <label
        className={`jt-rx-drop${file !== undefined ? ' jt-rx-drop--on' : ''}${fileMissing ? ' jt-rx-drop--err' : ''}`}
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          className="jt-rx-drop__input"
          aria-label="Prescription photo or PDF"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFile(event.target.files?.[0] ?? undefined);
            setFileMissing(false);
          }}
        />
        <span className="jt-rx-drop__icon" aria-hidden="true">
          <Icon d={file !== undefined ? IC.check : IC.upload} stroke="#5b2ede" size={18} />
        </span>
        <span className="jt-rx-drop__title">{file?.name ?? 'Add a photo or PDF'}</span>
        <span className="jt-rx-drop__sub">
          {file !== undefined
            ? 'Tap to choose a different file'
            : 'Make sure the whole prescription is clear and readable'}
        </span>
      </label>
      {fileMissing ? (
        <span className="jt-ord-err" role="alert">
          Add a photo or PDF of your prescription.
        </span>
      ) : null}
      <PharmacyPicker known={pharmacies} value={pharmacy} onChange={setPharmacy} />
      <label className="jt-rx-field">
        <span className="jt-rx-field__label">Anything the pharmacist should know (optional)</span>
        <textarea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      {failure !== undefined ? (
        <span className="jt-ord-err" role="alert">
          {failure}
        </span>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="jt-ord-mbtn jt-ord-mbtn--ink"
          disabled={busy}
          onClick={() => void send()}
        >
          {busy ? 'Sending…' : pharmacy !== undefined ? `Send to ${pharmacy.name}` : 'Send'}
        </button>
      </div>
    </ModalFrame>
  );
}

function RepeatModal({
  pharmacies,
  onClose,
  onSent,
}: {
  readonly pharmacies: readonly PharmacyChoice[];
  readonly onClose: () => void;
  readonly onSent: (request: PrescriptionRequest) => void;
}): JSX.Element {
  const [items, setItems] = useState<{ name: string; quantity: string }[]>([{ name: '', quantity: '' }]);
  const [pharmacy, setPharmacy] = useState<PharmacyChoice | undefined>(
    pharmacies.length === 1 ? pharmacies[0] : undefined,
  );
  const [gp, setGp] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();

  const named = items.filter((item) => item.name.trim() !== '');
  const send = async (): Promise<void> => {
    if (busy) return;
    setFailure(undefined);
    if (named.length === 0) {
      setFailure('Add the medicine you need.');
      return;
    }
    if (pharmacy === undefined) {
      setFailure('Choose the pharmacy to send it to.');
      return;
    }
    setBusy(true);
    try {
      const sent = await sendRequest({
        kind: 'REPEAT',
        storefrontId: pharmacy.id,
        items: named.map((item) => ({
          name: item.name.trim(),
          ...(item.quantity.trim() !== '' ? { quantity: item.quantity.trim() } : {}),
        })),
        ...(gp.trim() !== '' ? { gpPractice: gp.trim() } : {}),
        ...(phone.trim() !== '' ? { contactPhone: phone.trim() } : {}),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });
      onSent(sent);
    } catch (error) {
      setFailure(messageOf(error));
      setBusy(false);
    }
  };

  const setItem = (index: number, patch: Partial<{ name: string; quantity: string }>): void =>
    setItems((previous) => previous.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <ModalFrame
      title="Order a repeat"
      copy="Tell the pharmacy what you need. They contact your GP for the prescription and keep this page up to date."
      onClose={onClose}
    >
      <fieldset className="jt-rx-field">
        <legend className="jt-rx-field__label">Your medicines</legend>
        {items.map((item, index) => (
          <span key={index} className="jt-rx-item">
            <input
              type="text"
              aria-label={`Medicine ${String(index + 1)}`}
              placeholder="Name and strength, e.g. Amlodipine 5mg tablets"
              maxLength={120}
              value={item.name}
              onChange={(event) => setItem(index, { name: event.target.value })}
            />
            <input
              type="text"
              aria-label={`Quantity ${String(index + 1)}`}
              placeholder="How many"
              maxLength={60}
              value={item.quantity}
              onChange={(event) => setItem(index, { quantity: event.target.value })}
            />
          </span>
        ))}
        {items.length < 10 ? (
          <button
            type="button"
            className="jt-rx-add"
            onClick={() => setItems((previous) => [...previous, { name: '', quantity: '' }])}
          >
            <Icon d={IC.plus} size={14} width={2.6} /> Add another medicine
          </button>
        ) : null}
      </fieldset>
      <PharmacyPicker known={pharmacies} value={pharmacy} onChange={setPharmacy} />
      <label className="jt-rx-field">
        <span className="jt-rx-field__label">Your GP practice (optional)</span>
        <input type="text" maxLength={200} value={gp} onChange={(event) => setGp(event.target.value)} />
      </label>
      <label className="jt-rx-field">
        <span className="jt-rx-field__label">A phone number the pharmacy can call (optional)</span>
        <input type="tel" maxLength={30} value={phone} onChange={(event) => setPhone(event.target.value)} />
      </label>
      <label className="jt-rx-field">
        <span className="jt-rx-field__label">Anything else (optional)</span>
        <textarea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      {failure !== undefined ? (
        <span className="jt-ord-err" role="alert">
          {failure}
        </span>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="jt-ord-mbtn jt-ord-mbtn--ink"
          disabled={busy}
          onClick={() => void send()}
        >
          {busy
            ? 'Sending…'
            : named.length === 0
              ? 'Choose a medicine'
              : `Request ${String(named.length)} ${named.length === 1 ? 'item' : 'items'}`}
        </button>
      </div>
    </ModalFrame>
  );
}
